import { useMutation, useQuery } from "@tanstack/react-query";
import {
  createMaterialCode,
  createMaterialSupplier,
  createOtherCostProcess,
  deleteOtherCostProcess,
  getFormulars,
  getMaterialCodeById,
  getMaterialCodeByFirstTierSupplier,
  getMaterialCodes,
  getMaterialCodesTotal,
  getMaterialSupplierById,
  getMaterialSuppliers,
  getOtherCostProcesses,
  updateMaterialCode,
  updateMaterialSupplier,
  updateOtherCostProcess,
  type CreateMaterialCodePayload,
  type UpdateMaterialCodePayload,
  type MaterialCodePayload,
  type MaterialSupplierForm,
  type MaterialSupplierPayload,
} from "../api/materialSupplierService";
import { CACHE_TIME } from "@/lib/cacheConfig";
import { queryKeys } from "@/lib/queryKeys";
import { useQueryInvalidation } from "@/lib/queryInvalidation";

export const useGetMaterialSuppliers = (payload: MaterialSupplierPayload) => {
  return useQuery({
    queryKey: queryKeys.materialSupplier.list(payload),
    queryFn: () => getMaterialSuppliers(payload),
    staleTime: CACHE_TIME.SHORT,
  });
};

export const useGetFormulars = () => {
  return useQuery({
    queryKey: queryKeys.materialSupplier.formulars(),
    queryFn: getFormulars,
    staleTime: CACHE_TIME.MASTER_DATA,
  });
};

export const useGetMaterialSupplierById = (id?: number) => {
  return useQuery({
    queryKey: queryKeys.materialSupplier.detail(id),
    queryFn: () => getMaterialSupplierById(id),
    staleTime: CACHE_TIME.SHORT,
    enabled: !!id,
  });
};

export const useCreateMaterialSupplier = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.materialSupplier.create(),
    mutationFn: (params: MaterialSupplierForm) =>
      createMaterialSupplier(params),
    onSuccess: () => {
      queryInvalidation.invalidateMaterialSuppliers();
    },
  });
};

export const useUpdateMaterialSupplier = (id: number) => {
  const queryInvalidation = useQueryInvalidation();
  return useMutation({
    mutationKey: queryKeys.materialSupplier.update(id),
    mutationFn: (params: MaterialSupplierForm) =>
      updateMaterialSupplier(id, params),
    onSuccess: () => {
      queryInvalidation.invalidateMaterialSuppliers();
      queryInvalidation.invalidateMaterialCodes();
    },
  });
};

export const useGetMaterialCodeById = (id?: number) => {
  return useQuery({
    queryKey: queryKeys.materialSupplier.materialCodeById(id),
    queryFn: () => getMaterialCodeById(id!),
    staleTime: CACHE_TIME.SHORT,
    enabled: !!id,
  });
};

export const useGetMaterialCodes = (params: MaterialCodePayload) => {
  return useQuery({
    queryKey: queryKeys.materialSupplier.materialCodes(
      params.material_supplier_id,
      params.is_active,
      params.material_dimension_id,
      params.skip,
      params.limit,
      params.search,
    ),
    queryFn: () => getMaterialCodes(params),
    staleTime: CACHE_TIME.SHORT,
    enabled: !!params.material_supplier_id && !!params.material_dimension_id,
  });
};

export const useGetMaterialCodesTotal = (params: {
  material_supplier_id?: number;
  is_active?: boolean;
}) => {
  return useQuery({
    queryKey: queryKeys.materialSupplier.materialCodesTotal(
      params.material_supplier_id,
      params.is_active,
    ),
    queryFn: () => getMaterialCodesTotal(params),
    staleTime: CACHE_TIME.SHORT,
    enabled: !!params.material_supplier_id,
  });
};

export const useCreateMaterialCode = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.materialSupplier.createMaterialCode(),
    mutationFn: (params: CreateMaterialCodePayload) =>
      createMaterialCode(params),
    onSuccess: () => {
      queryInvalidation.invalidateMaterialCodes();
    },
  });
};

export const useUpdateMaterialCode = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.materialSupplier.updateMaterialCode(),
    mutationFn: (params: UpdateMaterialCodePayload[]) => updateMaterialCode(params),
    onSuccess: () => {
      queryInvalidation.invalidateMaterialCodes();
    },
  });
};

export const useGetOtherCostProcess = (materialSupplierId: number) => {
  return useQuery({
    queryKey:
      queryKeys.materialSupplier.getOtherCostProcesses(materialSupplierId),
    queryFn: () => getOtherCostProcesses(materialSupplierId),
    staleTime: CACHE_TIME.SHORT,
    enabled: !!materialSupplierId,
  });
};

export const useCreateOtherCostProcess = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.materialSupplier.createOtherCostProcess(),
    mutationFn: (params: {
      material_supplier_id: number;
      column_name: string;
      display_order: number;
    }) => createOtherCostProcess(params),
    onSuccess: () => {
      queryInvalidation.invalidateMaterialCodes();
      queryInvalidation.invalidateOtherCostProcesses();
    },
  });
};

export const useUpdateOtherCostProcess = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.materialSupplier.updateOtherCostProcess(0),
    mutationFn: (params: {
      material_supplier_id: number;
      column_id: number;
      column_name: string;
      display_order: number;
    }) =>
      updateOtherCostProcess(
        {
          material_supplier_id: params.material_supplier_id,
          column_name: params.column_name,
          display_order: params.display_order,
        },
        params.column_id
      ),
    onSuccess: () => {
      queryInvalidation.invalidateMaterialCodes();
      queryInvalidation.invalidateOtherCostProcesses();
    },
  });
};

export const useDeleteOtherCostProcess = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.materialSupplier.deleteOtherCostProcess(0),
    mutationFn: (params: {
      material_supplier_id: number;
      column_id: number;
    }) => {
      return deleteOtherCostProcess(
        params.material_supplier_id,
        params.column_id
      );
    },
    onSuccess: () => {
      queryInvalidation.invalidateMaterialCodes();
      queryInvalidation.invalidateOtherCostProcesses();
    },
  });
};

export const useGetMaterialCodeByFirstTierSupplier = (supplierCode: string) => {
  return useQuery({
    queryKey: queryKeys.materialSupplier.materialCodesBySupplier(supplierCode),
    queryFn: () => getMaterialCodeByFirstTierSupplier(supplierCode),
    staleTime: CACHE_TIME.SHORT,
    enabled: !!supplierCode,
  });
};